import type { Signal } from '@angular/core'
import { Injectable, signal } from '@angular/core'

@Injectable()
export class ZoomService {
  static readonly MIN_ZOOM = 0.25
  static readonly MAX_ZOOM = 3
  static readonly ZOOM_STEP = 0.5

  private readonly _zoomFactor = signal(1)
  readonly zoomFactor: Signal<number> = this._zoomFactor

  private readonly _panOffset = signal({ x: 0, y: 0 })
  readonly panOffset: Signal<{ x: number; y: number }> = this._panOffset

  constructor() {}

  /**
   * Increases the zoom factor by a fixed step.
   */
  zoomIn(): void {
    const nextZoom = Math.min(this._zoomFactor() + ZoomService.ZOOM_STEP, ZoomService.MAX_ZOOM)
    this._zoomFactor.set(Number(nextZoom.toFixed(2)))
  }

  /**
   * Decreases the zoom factor by a fixed step.
   */
  zoomOut(): void {
    const nextZoom = Math.max(this._zoomFactor() - ZoomService.ZOOM_STEP, ZoomService.MIN_ZOOM)
    this._zoomFactor.set(Number(nextZoom.toFixed(2)))
  }

  /**
   * Resets the zoom factor to 1
   */
  resetZoom(): void {
    this._zoomFactor.set(1)
    this._panOffset.set({ x: 0, y: 0 })
  }

  /**
   * Sets the zoom factor to a specific value
   * @param {number} zoom
   */
  setZoom(zoom: number): void {
    this._zoomFactor.set(Number(zoom.toFixed(2)))
    // Reset pan offset when zoom changes
    this._panOffset.set({ x: 0, y: 0 })
  }

  /**
   * Calculate the scale to fit the image within the viewport
   * @param {number | undefined} width Natural image width.
   * @param {number | undefined} height Natural image height.
   * @returns {number}
   */
  calculateFitScale(width: number | undefined, height: number | undefined): number {
    const viewportWidth = window.innerWidth * 0.8
    const viewportHeight = window.innerHeight * 0.8

    if (!width || !height) {
      return 1
    }

    // Calculate scale factors for both dimensions
    const scaleX = viewportWidth / width
    const scaleY = viewportHeight / height

    // Take the smaller scale factor to ensure the image fits within both dimensions
    const scale = Math.min(scaleX, scaleY)

    // Never upscale if the image is smaller than 80% of viewport
    return Math.min(scale, 1)
  }

  /**
   * Updates the pan offset
   * @param {number} deltaX Change in X position
   * @param {number} deltaY Change in Y position
   */
  updatePanOffset(deltaX: number, deltaY: number): void {
    const currentOffset = this._panOffset()
    this._panOffset.set({
      x: currentOffset.x + deltaX,
      y: currentOffset.y + deltaY
    })
  }

  /**
   * Sets the pan offset to a specific value
   * @param {number} x X offset
   * @param {number} y Y offset
   */
  setPanOffset(x: number, y: number): void {
    this._panOffset.set({ x, y })
  }

  /**
   * Resets the pan offset to center
   */
  resetPanOffset(): void {
    this._panOffset.set({ x: 0, y: 0 })
  }

  /**
   * Constrains the pan offset to prevent the image from being panned too far
   * @param {number} imageWidth Width of the image
   * @param {number} imageHeight Height of the image
   * @param {number} containerWidth Width of the container
   * @param {number} containerHeight Height of the container
   */
  constrainPanOffset(imageWidth: number, imageHeight: number, containerWidth: number, containerHeight: number): void {
    const zoomFactor = this._zoomFactor()
    const scaledImageWidth = imageWidth * zoomFactor
    const scaledImageHeight = imageHeight * zoomFactor

    const currentOffset = this._panOffset()

    let constrainedX = currentOffset.x
    let constrainedY = currentOffset.y

    if (scaledImageWidth > containerWidth) {
      // Image is larger than container, constrain to keep it visible
      const maxOffsetX = (scaledImageWidth - containerWidth) / 2

      constrainedX = Math.max(-maxOffsetX, Math.min(maxOffsetX, currentOffset.x))
    } else {
      // Image fits in container, center it
      constrainedX = 0
    }

    if (scaledImageHeight > containerHeight) {
      // Image is larger than container, constrain to keep it visible
      const maxOffsetY = (scaledImageHeight - containerHeight) / 2

      constrainedY = Math.max(-maxOffsetY, Math.min(maxOffsetY, currentOffset.y))
    } else {
      // Image fits in container, center it
      constrainedY = 0
    }

    this._panOffset.set({ x: constrainedX, y: constrainedY })
  }
}
